import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  RichTextEditorComponent,
  Inject,
  Toolbar,
  HtmlEditor,
  Image,
  Link,
  QuickToolbar,
  ToolbarType,
} from '@syncfusion/ej2-react-richtexteditor'
import type { ClickEventArgs } from '@syncfusion/ej2-react-navigations'
import './NoteTextEditor.css'
import { Col, Form, Row } from 'react-bootstrap'
import NotesDownloadNoteModal from '@components/pages/Notes/Modals/NotesDownloadNoteModal.component'
import { Button, DataGridComponent } from '@components/common'
import { NoteGridRow, NoteTextEditorProps } from './NoteTextEditor.types'
import { INote, NoteContextType } from '@dataTypes/note.type'
import { useNoteEditorState } from './hooks/useNoteEditorState'
import { useNoteImageUpload } from './hooks/useNoteImageUpload'
import { useSyncfusionTooltipCleanup } from './hooks/useSyncfusionTooltipCleanup'
import NoteEditorTopBar from './NoteEditorTopBar'
import NoteEditorBottomBar from './NoteEditorBottomBar'
import { createNotesGridColumns } from './notesGridColumns'
import { useNoteEditorOverlay } from '@components/common/NoteEditorOverlay'
import type { Patient } from '@dataTypes/PatientInfo.types'
import { fetchPatientById } from '@services/api/patient.api'
import { useAppSelector } from '@store/hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { createFailedToast } from '@utils/notificationToast.util'
import { formatClinicAddressForPdf } from '@utils/clinic.util'
import { downloadNoteAsPdf } from '@utils/notePdfExport.util'
import { getFullName } from '@utils/fullname.util'
import { formatDateUS } from '@utils/date.utils'
import {
  formatNoteTitleWithVersion,
  hasMultipleVersionsInChain,
} from '@utils/noteVersion.util'

export const getPreviewContentText = (html: string): string => {
  // 1) Convert HTML → text. Prefer innerText because it inserts line breaks for block elements.
  const container = document.createElement('div')
  container.innerHTML = html

  const containerWithInnerText = container as HTMLElement & {
    innerText?: string
  }
  let plain: string =
    containerWithInnerText.innerText ?? container.textContent ?? ''

  // 2) Some environments may still collapse lines on detached nodes.
  //    As a safety net, inject explicit \n for common block endings, then read again.
  if (!plain.includes('\n')) {
    const withBreaks = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p\s*>/gi, '\n')
      .replace(/<\/div\s*>/gi, '\n')
      .replace(/<\/li\s*>/gi, '\n')
      .replace(/<\/h[1-6]\s*>/gi, '\n')
    const tmp = document.createElement('div')
    tmp.innerHTML = withBreaks
    const tmpWithInnerText = tmp as HTMLElement & { innerText?: string }
    plain = tmpWithInnerText.innerText ?? tmp.textContent ?? ''
  }

  // 3) Normalize whitespace (nbsp, multiple spaces/newlines)
  plain = plain
    .replace(/\r/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t\f\v]+/g, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim()

  // 4) Return the first non-empty line only
  const firstLine = plain.split('\n').find((l) => l.trim().length > 0) || ''
  return firstLine
}

const buildHtmlDoc = (content: string, docTitle: string): string => {
  const safeTitle = docTitle.replace(/[<>"]/g, '')
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${safeTitle}</title>
<style>
/* Optional minimal print-friendly defaults */
body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; line-height: 1.5; margin: 2rem; color: #111; }
img { max-width: 100%; height: auto; }
blockquote { margin: 1rem 0; padding-left: 1rem; border-left: 4px solid #ddd; color: #444; }
</style>
</head>
<body>
${content}
</body>
</html>`
}

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const downloadHtml = (html: string, title: string): void => {
  const fullDoc = buildHtmlDoc(html, title)

  const blob = new Blob([fullDoc], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = `${slugify(title) || 'document'}.html`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

const PDF_DOWNLOAD_LOADING_CONTAINER = 'body'

const toIsoOptional = (
  value: Date | string | undefined,
): string | undefined => {
  if (value == null) return undefined
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'string') return value
  return undefined
}

const gridRowToNoteForPdf = (row: NoteGridRow, clinicId: string): INote => {
  const rawCtx = row.context_type
  const contextType =
    rawCtx != null &&
    (Object.values(NoteContextType) as string[]).includes(rawCtx)
      ? (rawCtx as NoteContextType)
      : NoteContextType.PATIENT

  return {
    note_id: row.note_id ?? '',
    clinic_id: clinicId,
    title: row.title ?? 'Untitled',
    body: row.content ?? row.body ?? '',
    context_type: contextType,
    context_id: row.context_id ?? null,
    patient_id: row.patient_id ?? null,
    contact_id: row.contact_id ?? null,
    created_by: '',
    date_time_created_at: toIsoOptional(row.date_time_created_at),
    date_time_updated_at: toIsoOptional(row.date_time_updated_at),
  }
}

const items = [
  'FontSize',
  'FontColor',
  'BackgroundColor',
  '|',
  'Alignments',
  '|',
  'Bold',
  'Italic',
  'Underline',
  'StrikeThrough',
  '|',
  'Image',
]

const idToCmd = {
  'tb-undo': 'undo',
  'tb-redo': 'redo',
} as const

type CommandName = (typeof idToCmd)[keyof typeof idToCmd]

const NoteTextEditor: React.FC<NoteTextEditorProps> = ({
  patientId,
  contextId,
  overlayContextName = null,
  notesOverlayPortalTarget,
  notesOverlayAnchorClassName,
  notesOverlayBodyClassName,
  noteData,
  notes,
  viewMode = false,
  onUpdate,
  onSave,
  settingsData = {
    showCaption: true,
  },
  rightTopEditorButtons,
  showEditor = true,
  alwaysShowSavedNotesSection = false,
  savedNotesAllowDelete = true,
  savedNotesUsePdfDownload = false,
  savedNotesPatientDisplayName,
  onAddNote,
  onViewNote,
  onEditSavedNote,
  onNoteSavedImmediate,
  onNoteMutationSuccess,
  claimStatusForNotes,
}) => {
  const { openNoteEditor, updateNoteEditor } = useNoteEditorOverlay()
  const rteRef = useRef<RichTextEditorComponent>(null)
  const noteImageInputRef = useRef<HTMLInputElement>(null)
  const [settings] = useState<NoteTextEditorProps['settingsData']>(settingsData)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [noteRowPendingPdfDownload, setNoteRowPendingPdfDownload] =
    useState<NoteGridRow | null>(null)
  const {
    data,
    notesData,
    savedNotesGridKey,
    defaultTag,
    viewOnly,
    setData,
    handleSaveNote,
    handleEditNote,
    handleViewNote,
    handleDeleteNote,
  } = useNoteEditorState({
    patientId,
    contextId,
    noteData,
    notes,
    viewMode,
    onUpdate,
  })

  useSyncfusionTooltipCleanup()

  const savedNotesPdfPatientName = useMemo((): string => {
    const explicit = savedNotesPatientDisplayName?.trim()
    if (explicit) return explicit
    if (selectedPatient?.patient_id === patientId) {
      return getFullName(selectedPatient) || 'Unknown'
    }
    return 'Unknown'
  }, [patientId, savedNotesPatientDisplayName, selectedPatient])

  const handleSavedNoteDownloadClick = useCallback(
    (props: NoteGridRow): void => {
      if (savedNotesUsePdfDownload) {
        setNoteRowPendingPdfDownload(props)
        return
      }
      downloadHtml(props.content ?? props.body ?? '', props.title ?? '')
    },
    [savedNotesUsePdfDownload],
  )

  const handleConfirmSavedNotePdfDownload = useCallback((): void => {
    const row = noteRowPendingPdfDownload
    setNoteRowPendingPdfDownload(null)
    if (!row || !savedNotesUsePdfDownload) return

    void (async (): Promise<void> => {
      try {
        showLoading({ container: PDF_DOWNLOAD_LOADING_CONTAINER })
        const clinicName =
          selectedClinic?.clinic_name?.trim() || 'EnamelAI Clinic'
        const clinicAddress = selectedClinic
          ? formatClinicAddressForPdf(selectedClinic)
          : ''
        const note = gridRowToNoteForPdf(row, selectedClinic?.clinic_id ?? '')
        await downloadNoteAsPdf({
          note,
          patientName: savedNotesPdfPatientName,
          clinicName,
          clinicAddress,
          clinicLogoUrl: selectedClinic?.logo_url,
        })
      } catch {
        showToast(
          createFailedToast(
            'Download note',
            'Failed to download note. Please try again.',
          ),
        )
      } finally {
        hideLoading(PDF_DOWNLOAD_LOADING_CONTAINER)
      }
    })()
  }, [
    noteRowPendingPdfDownload,
    savedNotesPdfPatientName,
    savedNotesUsePdfDownload,
    selectedClinic,
    showLoading,
    hideLoading,
    showToast,
  ])

  const exec = useCallback(
    (cmd: CommandName): void => rteRef.current?.executeCommand(cmd),
    [],
  )

  const { onActionBegin, handleNoteImageSelect } = useNoteImageUpload({
    rteRef,
    noteImageInputRef,
    setData,
    onUpdate: notes ? undefined : onUpdate,
  })

  const onBottomToolbarClick = useCallback(
    (args: ClickEventArgs): void => {
      const itemId = args?.item?.id
      if (itemId === 'tb-save') {
        if (onSave) {
          onSave()
        } else {
          handleSaveNote()
        }
        return
      }

      const cmd = idToCmd[itemId as keyof typeof idToCmd]
      if (cmd) {
        exec(cmd)
      }
    },
    [exec, handleSaveNote, onSave],
  )

  const onActionComplete = useCallback((): void => {
    setData((prev) => ({
      ...prev,
      content: rteRef.current?.value ?? '',
    }))
  }, [setData])

  const hasPersistedNotes = useMemo(
    () =>
      (notes ?? []).some(
        (n) => typeof n.note_id === 'string' && n.note_id.trim() !== '',
      ),
    [notes],
  )
  const canAddViaOverlay = typeof onAddNote === 'function'
  const useOverlayForSavedNotesGrid =
    alwaysShowSavedNotesSection || canAddViaOverlay
  const shouldRenderSavedNotesSection =
    alwaysShowSavedNotesSection || hasPersistedNotes || canAddViaOverlay
  const shouldRenderInlineEditor =
    !shouldRenderSavedNotesSection && (showEditor || viewOnly)

  const resolveContextType = useCallback(
    (value: string | undefined): NoteContextType | undefined => {
      if (value == null) return undefined
      return (Object.values(NoteContextType) as string[]).includes(value)
        ? (value as NoteContextType)
        : undefined
    },
    [],
  )

  const resolveOverlayPatient =
    useCallback(async (): Promise<Patient | null> => {
      if (selectedPatient?.patient_id === patientId) {
        return selectedPatient as unknown as Patient
      }
      try {
        const response = await fetchPatientById(patientId)
        if (response.statusCode !== 200 || !response.data?.patient) {
          return null
        }
        return response.data.patient as unknown as Patient
      } catch {
        return null
      }
    }, [patientId, selectedPatient])

  const handleAddNoteNavigate = useCallback((): void => {
    if (onAddNote) {
      onAddNote()
      return
    }
    const noteContextType = resolveContextType(noteData?.tag)
    if (notesOverlayBodyClassName) {
      document.body.classList.add(notesOverlayBodyClassName)
    }
    openNoteEditor({
      patient: null,
      contextId: contextId ?? null,
      contextType: noteContextType ?? null,
      contextName: overlayContextName ?? null,
      contactId: null,
      openFirstNote: false,
      initialView: 'add',
      overlayBackLabel: 'Back',
      editorBackButtonLabel: 'Back',
      openContextNotesByDefault: true,
      notesFetchLoadingContainer: 'body',
      portalTarget: notesOverlayPortalTarget ?? '.page-content',
      anchorClassName: notesOverlayAnchorClassName,
      onOverlayWillClose: () => {
        if (notesOverlayBodyClassName) {
          document.body.classList.remove(notesOverlayBodyClassName)
        }
      },
      onNoteSavedImmediate,
      onNoteMutationSuccess,
      claimStatusForNotes,
    })
    void resolveOverlayPatient().then((overlayPatient) => {
      updateNoteEditor({ patient: overlayPatient })
    })
  }, [
    contextId,
    notesOverlayAnchorClassName,
    notesOverlayBodyClassName,
    overlayContextName,
    noteData?.tag,
    notesOverlayPortalTarget,
    onAddNote,
    claimStatusForNotes,
    onNoteMutationSuccess,
    onNoteSavedImmediate,
    openNoteEditor,
    resolveContextType,
    resolveOverlayPatient,
    updateNoteEditor,
  ])

  const handleViewNoteNavigate = useCallback(
    (props: Parameters<typeof handleViewNote>[0]): void => {
      if (onViewNote) {
        onViewNote(props)
        return
      }
      const contextType = resolveContextType(props.context_type)
      const rowContextId =
        typeof props.context_id === 'string' && props.context_id.trim() !== ''
          ? props.context_id
          : undefined
      const openNoteId =
        typeof props.note_id === 'string' && props.note_id.trim() !== ''
          ? props.note_id
          : undefined

      if (notesOverlayBodyClassName) {
        document.body.classList.add(notesOverlayBodyClassName)
      }
      openNoteEditor({
        patient: null,
        contextId: rowContextId ?? null,
        contextType: contextType ?? null,
        contextName: overlayContextName ?? null,
        contactId: null,
        openFirstNote: true,
        openNoteId: openNoteId ?? null,
        initialView: 'list',
        hideNotesTable: true,
        overlayBackLabel: 'Back',
        editorBackButtonLabel: 'Back',
        openContextNotesByDefault: true,
        notesFetchLoadingContainer: 'body',
        portalTarget: notesOverlayPortalTarget ?? '.page-content',
        anchorClassName: notesOverlayAnchorClassName,
        onOverlayWillClose: () => {
          if (notesOverlayBodyClassName) {
            document.body.classList.remove(notesOverlayBodyClassName)
          }
        },
        onNoteSavedImmediate,
        onNoteMutationSuccess,
        claimStatusForNotes,
      })
      void resolveOverlayPatient().then((overlayPatient) => {
        updateNoteEditor({ patient: overlayPatient })
      })
    },
    [
      notesOverlayAnchorClassName,
      notesOverlayBodyClassName,
      notesOverlayPortalTarget,
      overlayContextName,
      claimStatusForNotes,
      onNoteMutationSuccess,
      onNoteSavedImmediate,
      onViewNote,
      openNoteEditor,
      resolveContextType,
      resolveOverlayPatient,
      updateNoteEditor,
    ],
  )

  const handleEditNoteNavigate = useCallback(
    (props: NoteGridRow): void => {
      if (onEditSavedNote) {
        onEditSavedNote(props)
        return
      }
      const contextType = resolveContextType(props.context_type)
      const rowContextId =
        typeof props.context_id === 'string' && props.context_id.trim() !== ''
          ? props.context_id
          : undefined
      const openNoteId =
        typeof props.note_id === 'string' && props.note_id.trim() !== ''
          ? props.note_id
          : undefined

      if (notesOverlayBodyClassName) {
        document.body.classList.add(notesOverlayBodyClassName)
      }
      openNoteEditor({
        patient: null,
        contextId: rowContextId ?? null,
        contextType: contextType ?? null,
        contextName: overlayContextName ?? null,
        contactId: null,
        openFirstNote: true,
        openNoteId: openNoteId ?? null,
        openNoteInEditMode: true,
        initialView: 'list',
        hideNotesTable: true,
        overlayBackLabel: 'Back',
        editorBackButtonLabel: 'Back',
        openContextNotesByDefault: true,
        notesFetchLoadingContainer: 'body',
        portalTarget: notesOverlayPortalTarget ?? '.page-content',
        anchorClassName: notesOverlayAnchorClassName,
        onOverlayWillClose: () => {
          if (notesOverlayBodyClassName) {
            document.body.classList.remove(notesOverlayBodyClassName)
          }
        },
        onNoteSavedImmediate,
        onNoteMutationSuccess,
        claimStatusForNotes,
      })
      void resolveOverlayPatient().then((overlayPatient) => {
        updateNoteEditor({ patient: overlayPatient })
      })
    },
    [
      notesOverlayAnchorClassName,
      notesOverlayBodyClassName,
      notesOverlayPortalTarget,
      overlayContextName,
      onEditSavedNote,
      claimStatusForNotes,
      onNoteMutationSuccess,
      onNoteSavedImmediate,
      openNoteEditor,
      resolveContextType,
      resolveOverlayPatient,
      updateNoteEditor,
    ],
  )

  const columnsNotes = useMemo(() => {
    return createNotesGridColumns({
      showEditor,
      versionChainPool: (notesData ?? []) as NoteGridRow[],
      onView: useOverlayForSavedNotesGrid
        ? handleViewNoteNavigate
        : handleViewNote,
      onEdit: useOverlayForSavedNotesGrid
        ? handleEditNoteNavigate
        : handleEditNote,
      onDelete: handleDeleteNote,
      onDownload: handleSavedNoteDownloadClick,
      allowDelete: savedNotesAllowDelete,
    })
  }, [
    handleDeleteNote,
    handleEditNote,
    handleEditNoteNavigate,
    handleSavedNoteDownloadClick,
    handleViewNote,
    handleViewNoteNavigate,
    notesData,
    savedNotesAllowDelete,
    showEditor,
    useOverlayForSavedNotesGrid,
  ])

  return (
    <>
      {shouldRenderInlineEditor && (
        <Row className="gx-2 note-text-editor-container">
          <Col md="12" className="mb-3">
            {settings?.showCaption && showEditor && (
              <Form.Label className="editor-caption-label">
                Add note <span className="form-label-grey">(Optional)</span>
              </Form.Label>
            )}
            <div className="note-text-editor">
              <NoteEditorTopBar
                data={data}
                setData={setData}
                viewOnly={viewOnly}
                isEditingTitle={isEditingTitle}
                setIsEditingTitle={setIsEditingTitle}
                rightTopEditorButtons={rightTopEditorButtons}
              />
              <input
                ref={noteImageInputRef}
                type="file"
                accept="image/*"
                className="d-none"
                aria-hidden
                onChange={handleNoteImageSelect}
              />
              <RichTextEditorComponent
                ref={rteRef}
                value={data.content}
                toolbarSettings={{
                  enable: !viewOnly,
                  items,
                  type: 'MultiRow' as ToolbarType,
                }}
                actionBegin={onActionBegin}
                actionComplete={onActionComplete}
                change={onActionComplete}
                enabled={!viewOnly}
                enableHtmlSanitizer={true}
              >
                <Inject
                  services={[Toolbar, HtmlEditor, Image, Link, QuickToolbar]}
                />
              </RichTextEditorComponent>
              <NoteEditorBottomBar
                viewOnly={viewOnly}
                defaultTag={defaultTag}
                tag={data.tag}
                date={data.date}
                showSaveAction={false}
                onBottomToolbarClick={onBottomToolbarClick}
              />
            </div>
          </Col>
        </Row>
      )}
      {shouldRenderSavedNotesSection && (
        <Row className="gx-2">
          <Col
            md={12}
            className="grid-procedures-container note-text-editor-table"
          >
            <div className="d-flex justify-content-between align-items-center mb-1">
              <Form.Label className="saved-notes-caption-label mb-0">
                Saved notes
              </Form.Label>
              <Button
                size="md"
                className="button-secondary add-note-button"
                onClick={handleAddNoteNavigate}
                type="button"
              >
                Add note
              </Button>
            </div>
            <DataGridComponent
              key={savedNotesGridKey}
              data={(notesData ?? []).map((note) => ({
                ...note,
                note_title: hasMultipleVersionsInChain(note, notesData ?? [])
                  ? formatNoteTitleWithVersion({
                      title: note.title ?? '',
                      version: note.version,
                    })
                  : note.title,
                date_time_updated_at_text: formatDateUS(
                  (note.date_time_updated_at as string) ?? '',
                ),
                date_time_created_at_text: formatDateUS(
                  (note.date_time_created_at as string) ?? '',
                ),
              }))}
              columns={columnsNotes}
              rowHeight={32}
              allowTextWrap={false}
              allowPaging={true}
              pageSize={10}
            />
          </Col>
        </Row>
      )}

      <NotesDownloadNoteModal
        show={Boolean(savedNotesUsePdfDownload && noteRowPendingPdfDownload)}
        noteName={noteRowPendingPdfDownload?.title ?? ''}
        onCancel={() => setNoteRowPendingPdfDownload(null)}
        onDownload={handleConfirmSavedNotePdfDownload}
      />
    </>
  )
}

export default NoteTextEditor
